import { defineStore } from 'pinia';
import { type Invite, type InviteType } from '../models/invite';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface InviteStoreState {
	received: Invite[];
	sent: Invite[];
	loading: boolean;
}

export const useInviteStore = defineStore('InviteStore', {
	state: (): InviteStoreState => ({
		received: [],
		sent: [],
		loading: false,
	}),

	getters: {
		pendingReceived: (state) => state.received,
		pendingReceivedByType: (state) => (type: InviteType) =>
			state.received.filter(i => i.type === type),
		pendingSent: (state) => state.sent,
	},

	actions: {
		async fetchReceived() {
			this.loading = true;
			const functions = getFunctions();
			const fetch = httpsCallable<object, { invites: Invite[] }>(functions, 'fetchMyInvites');
			const result = await fetch({});
			this.received = result.data.invites;
			this.loading = false;
		},

		async fetchSent() {
			const functions = getFunctions();
			const fetch = httpsCallable<object, { invites: Invite[] }>(functions, 'fetchSentInvites');
			const result = await fetch({});
			this.sent = result.data.invites;
		},

		// Character sharing
		async sendCharacterShare(toAlias: string, characterId: string, message?: string) {
			const functions = getFunctions();
			const send = httpsCallable<{ toAlias: string; characterId: string; message?: string }, { success: boolean; inviteId: string }>(functions, 'sendCharacterShare');
			const result = await send({ toAlias, characterId, message });
			await this.fetchSent();
			return result.data;
		},

		async acceptCharacterShare(inviteId: string) {
			const functions = getFunctions();
			const accept = httpsCallable<{ inviteId: string }, { success: boolean }>(functions, 'acceptCharacterShare');
			await accept({ inviteId });
			this.received = this.received.filter(i => i.id !== inviteId);
		},

		async declineCharacterShare(inviteId: string) {
			const functions = getFunctions();
			const decline = httpsCallable<{ inviteId: string }, { success: boolean }>(functions, 'declineCharacterShare');
			await decline({ inviteId });
			this.received = this.received.filter(i => i.id !== inviteId);
		},

		async revokeCharacterShare(characterId: string, recipientAlias: string) {
			const functions = getFunctions();
			const revoke = httpsCallable<{ characterId: string; recipientAlias: string }, { success: boolean }>(functions, 'revokeCharacterShare');
			await revoke({ characterId, recipientAlias });
		},

		async removeCharacterShare(shareId: string) {
			const functions = getFunctions();
			const remove = httpsCallable<{ shareId: string }, { success: boolean }>(functions, 'removeCharacterShare');
			await remove({ shareId });
		},

		// Game invites
		async sendGameInvite(gameId: string, toAlias: string, message?: string) {
			const functions = getFunctions();
			const send = httpsCallable<{ gameId: string; toAlias: string; message?: string }, { success: boolean; inviteId: string }>(functions, 'sendGameInvite');
			const result = await send({ gameId, toAlias, message });
			await this.fetchSent();
			return result.data;
		},

		async acceptGameInvite(inviteId: string, characterId?: string) {
			const functions = getFunctions();
			const accept = httpsCallable<{ inviteId: string; characterId?: string }, { success: boolean }>(functions, 'acceptGameInvite');
			await accept({ inviteId, characterId });
			this.received = this.received.filter(i => i.id !== inviteId);
		},

		async declineGameInvite(inviteId: string) {
			const functions = getFunctions();
			const decline = httpsCallable<{ inviteId: string }, { success: boolean }>(functions, 'declineGameInvite');
			await decline({ inviteId });
			this.received = this.received.filter(i => i.id !== inviteId);
		},

		// Generic cancel (sender cancels any pending invite)
		async cancelInvite(inviteId: string) {
			const functions = getFunctions();
			const cancel = httpsCallable<{ inviteId: string }, { success: boolean }>(functions, 'cancelInvite');
			await cancel({ inviteId });
			this.sent = this.sent.filter(i => i.id !== inviteId);
		},

		// Alias search (already uses Cloud Function)
		async searchAliases(searchText: string, filterTag?: string): Promise<{ alias: string; tags: string[] }[]> {
			if (!searchText || searchText.length < 2) return [];
			const functions = getFunctions();
			const search = httpsCallable<{ searchText: string; filterTag?: string }, { results: { alias: string; tags: string[] }[] }>(functions, 'searchAliases');
			const result = await search({ searchText, filterTag: filterTag || undefined });
			return result.data.results;
		},
	},
});
